import React, { useEffect, useState } from "react";
import { Input } from "antd";
import {
  useAllOrders,
  useSearchOrders, // Note: This hook is not needed with the fix below
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Rto = ({ status = "returns" }) => {
  const [searchText, setSearchText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { setLoading } = useLoader();

  // Pass currentPage and searchQuery directly to the API hook
  const {
    data: allOrdersData,
    isLoading: isAllLoading,
    isError,
    refetch,
  } = useAllOrders({
    currentPage,
    ORDERS_PER_PAGE,
    status,
    search: searchQuery, // Pass the search query to the API call
  });

  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearchQuery(searchText.trim());
      setCurrentPage(1); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(debounce);
  }, [searchText]);

  const isLoading = isAllLoading;
  const orders = allOrdersData?.orders || [];
  const totalPages = allOrdersData?.totalPages || 1;

  if (isError) {
    return <p className="text-red-600 mt-4">Error fetching orders</p>;
  }

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">AWB Number/Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer Name / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier Partner / Tracking</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-500">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.awbNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatus === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatus === "in_transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatus === "shipped"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {order.shippingStatus}
                    </div>
                  </td>
                  <td className="p-4">
                    <p>₹{order.total_amount}</p>
                    <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-1 rounded mt-1">
                      {order.PaymentType}
                    </span>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.phone}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.city}</p>
                    <p>{order.BillingAddress?.state}</p>
                    <p>{order.BillingAddress?.pincode}</p>
                  </td>
                  <td className="p-4">
                    <p>{order?.courier}</p>
                    {order.awbNumber ? (
                      <a
                        href={`https://shipclues.com/track-order/${order.awbNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors duration-200"
                      >
                        Track Order
                      </a>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded">
                        AWB Not Available
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-400">
                  No RTO orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && orders.length > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded border ${
                currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rto;
